/**
 * Speech mesh-capability contract — PoC (browser mic → ASR → transcript,
 * and text → TTS → audio clip, each exposed as a `tc` tool-call job
 * rather than an activation pipeline).
 *
 * A peer that can transcribe audio advertises `ASR_SKILL` in
 * `cap.skills[]` and a `transcribe` tool descriptor (name `ASR_TOOL_NAME`)
 * in `cap.tools[]` — same shape as any other opt-in tool (see `tools.ts`).
 * An asker finds such a peer via the roster, ships an `AsrTranscribeArgs`
 * payload over `tc`, and gets back `AsrTranscribeContent` on the matching
 * `MeshToolResult`. A peer that can synthesize speech advertises
 * `TTS_SKILL` + a `synthesize` tool descriptor (`TTS_TOOL_NAME`) the same
 * way, taking `TtsSynthesizeArgs` and returning `TtsSynthesizeContent`.
 *
 * Wire framing: audio rides `tc` (JSON) as a base64 string for this PoC —
 * both directions (ASR's input clip, TTS's output clip). That's simple
 * and interoperable with the existing tool-call bus, but it's not the
 * long-term shape — base64-in-JSON has ~33% overhead and no streaming.
 * The phase-2 upgrade is to carry raw PCM/opus bytes as a binary Codec
 * `LatentFrame` (see `wire.ts` / `webrtc-codec.ts`), the way the mesh
 * already ships token/activation frames, instead of stuffing bytes into
 * a JSON string. Not implemented here — this PoC intentionally keeps the
 * transport dumb so the ASR/TTS engines + tool-call plumbing can be
 * proven first.
 */
/** Skill name an ASR-capable peer puts in `cap.skills[]`. */
export const ASR_SKILL = 'asr.transcribe';
/** Tool name an ASR-capable peer registers + advertises in `cap.tools[]`. */
export const ASR_TOOL_NAME = 'transcribe';
/** Skill name a TTS-capable peer puts in `cap.skills[]`. */
export const TTS_SKILL = 'tts.synthesize';
/** Tool name a TTS-capable peer registers + advertises in `cap.tools[]`. */
export const TTS_TOOL_NAME = 'synthesize';
